import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createFeatureFlag,
  deleteFeatureFlag,
  fetchFlagStats,
  flagMeta,
  FLAG_TEMPLATES,
  listFeatureFlags,
  setFeatureFlag,
  type CoreFeatureFlag,
} from '@/lib/coreFeatureFlags';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function FeatureFlagsTab({ org }: Props) {
  const [flags, setFlags] = useState<CoreFeatureFlag[]>([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, progressive: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState({ flagKey: '', label: '', description: '', rollout: 0 });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [f, s] = await Promise.all([listFeatureFlags(org.id), fetchFlagStats(org.id)]);
      setFlags(f);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (f: CoreFeatureFlag) => {
    setBusy(f.flag_key);
    try {
      await setFeatureFlag({ tenantId: org.tenant_id, organizationId: org.id, flagKey: f.flag_key, enabled: !f.enabled });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setBusy(null);
    }
  };

  const setRollout = async (f: CoreFeatureFlag, value: number) => {
    setBusy(f.flag_key);
    try {
      await setFeatureFlag({ tenantId: org.tenant_id, organizationId: org.id, flagKey: f.flag_key, rolloutPercentage: value, enabled: value > 0 ? true : f.enabled });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    const key = form.flagKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!key) { setError('La clé du flag est requise.'); return; }
    setCreating(true);
    setError('');
    try {
      await createFeatureFlag({ organizationId: org.id, flagKey: key, label: form.label || key, description: form.description || null, rolloutPercentage: form.rollout, enabled: true });
      setForm({ flagKey: '', label: '', description: '', rollout: 0 });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (f: CoreFeatureFlag) => {
    if (f.id < 0) return;
    setBusy(f.flag_key);
    try { await deleteFeatureFlag(f.id); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Feature flags</span> : activer progressivement certaines fonctionnalités —
        <code className="bg-background-100 px-1 rounded mx-1">new_ai_booking_engine · advanced_inventory · new_dashboard · mobile_payments</code>.
        Le déploiement progressif se fait via un pourcentage de mise à disposition.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-toggle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Flags</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.enabled}</p>
          <p className="text-xs text-foreground-500">Activés</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-contrast-drop-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.progressive}</p>
          <p className="text-xs text-foreground-500">Déploiement progressif</p>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-toggle-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Feature flags</h4>
          <button onClick={load} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-background-100 text-foreground-700 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"><i className="ri-refresh-line"></i> Actualiser</button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {flags.map((f) => {
              const meta = flagMeta(f.flag_key);
              const pct = f.rollout_percentage ?? 0;
              return (
                <div key={f.flag_key} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.enabled ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}><i className={meta.icon}></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate">{f.label || meta.label}</p>
                      <p className="text-[11px] text-foreground-400 truncate"><code className="bg-background-100 px-1 rounded">{f.flag_key}</code> · {f.scope || 'platform'}{f.organization_id ? ' · entreprise' : ' · plateforme'}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${f.enabled ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-500'}`}>{f.enabled ? 'Activé' : 'Désactivé'}</span>
                    {busy === f.flag_key && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                    <button onClick={() => toggle(f)} disabled={busy === f.flag_key} className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 cursor-pointer ${f.enabled ? 'bg-primary-500' : 'bg-background-200'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${f.enabled ? 'left-[26px]' : 'left-0.5'}`}></span>
                    </button>
                    {f.id > 0 && (
                      <button onClick={() => remove(f)} className="w-7 h-7 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-600 transition-colors">
                        <i className="ri-delete-bin-6-line text-xs"></i>
                      </button>
                    )}
                  </div>
                  {f.enabled && (
                    <div className="mt-2.5 pl-12 flex items-center gap-3">
                      <span className="text-[11px] text-foreground-500 whitespace-nowrap w-28">Déploiement {pct}%</span>
                      <input type="range" min={0} max={100} step={10} defaultValue={pct} onMouseUp={(e) => setRollout(f, Number((e.target as HTMLInputElement).value))} onTouchEnd={(e) => setRollout(f, Number((e.target as HTMLInputElement).value))} className="flex-1 accent-primary-500 cursor-pointer" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Créer */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Créer un flag</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {FLAG_TEMPLATES.map((t) => (
              <button key={t.key} onClick={() => setForm({ ...form, flagKey: t.key, label: t.label, description: t.desc })} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors flex items-center gap-1"><i className={t.icon}></i>{t.key}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={INPUT} placeholder="Clé (ex : new_checkout)" value={form.flagKey} onChange={(e) => setForm({ ...form, flagKey: e.target.value })} />
            <input className={INPUT} placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>
          <input className={INPUT} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-foreground-500 whitespace-nowrap w-28">Déploiement {form.rollout}%</span>
            <input type="range" min={0} max={100} step={10} value={form.rollout} onChange={(e) => setForm({ ...form, rollout: Number(e.target.value) })} className="flex-1 accent-primary-500 cursor-pointer" />
          </div>
          <button onClick={create} disabled={creating || !form.flagKey.trim()} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {creating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Créer le flag
          </button>
        </div>
      </div>
    </div>
  );
}